import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ChevronDown,
  Loader2,
  Shield,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileUploadSection } from "@/components/onboarding/shared/FileUploadSection";
import LockedSectionNotice from "@/components/shared/LockedSectionNotice";
import { CyFunLevelBadge } from "@/components/risk/CyFunLevelBadge";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useNotificationGuideProgress } from "@/hooks/api/query/useNotificationGuideProgress";
import { useGetRiskAnalysisBySession } from "@/hooks/api/query/useGetRiskAnalysisBySession";
import { useBulkInsertRiskAnalysis } from "@/hooks/api/mutate/useBulkInsertRiskAnalysis";
import { useBulkDeleteRiskAnalysis } from "@/hooks/api/mutate/useBulkDeleteRiskAnalysis";
import { extractDataFromFiles } from "@/lib/file-extraction";
import { isRiskAnalysisUnlocked } from "@/utils/guideProgressCalculator";
import {
  mapExtractedSectorsToBulkItems,
  type MappedRiskAnalysisResult,
} from "@/utils/riskExtractionMapping";
import { useToast } from "@/hooks/use-toast";
import type { RiskAnalysisTransformResult } from "@/lib/file-extraction/types";
import { OnboardingFooter } from "@/components/onboarding/shared/OnboardingFooter";
import type { CyFunLevel } from "@/types/risk-analysis";
import type { ModificationRow } from "@/utils/riskMatrixModifications";
import { normalizeCui } from "@/utils/cuiUtils";

function SectorModifications({
  modifications,
}: {
  modifications: ModificationRow[];
}) {
  if (modifications.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        Valori implicite DNSC
      </p>
    );
  }

  return (
    <div className="space-y-1 pt-2">
      <p className="text-xs font-medium text-slate-600">
        {modifications.length}{" "}
        {modifications.length === 1 ? "modificare" : "modificări"} față de
        valorile implicite DNSC:
      </p>
      <div className="divide-y">
        {modifications.map((mod) => (
          <div
            key={mod[2]}
            className="flex items-center justify-between gap-2 text-xs py-1.5"
          >
            <span className="text-slate-600 min-w-0">{mod[0]}</span>
            <span className="text-muted-foreground shrink-0">{mod[2]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AnalizaRisc() {
  const navigate = useNavigate();
  const { currentSessionId } = useSessionSwitcher();
  const { toast } = useToast();

  const { isLoading: isLoadingProgress, entityData } =
    useNotificationGuideProgress(currentSessionId);

  const { data: existingRiskData } =
    useGetRiskAnalysisBySession(currentSessionId);

  const bulkInsertMutation = useBulkInsertRiskAnalysis();
  const bulkDeleteMutation = useBulkDeleteRiskAnalysis();

  const [extractedData, setExtractedData] =
    useState<RiskAnalysisTransformResult | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [pendingAction, setPendingAction] = useState<"save" | "wizard" | null>(
    null
  );

  const hasExistingRiskData =
    (existingRiskData?.risk_analyses?.length ?? 0) > 0;
  const isSaving = bulkInsertMutation.isPending || bulkDeleteMutation.isPending;

  const notificareCompleted =
    !isLoadingProgress && isRiskAnalysisUnlocked(entityData);

  const sessionCui = normalizeCui(entityData?.cui);
  const extractedCui = normalizeCui(extractedData?.entityData?.cui);
  const cuiMismatch =
    sessionCui !== "" && extractedCui !== "" && sessionCui !== extractedCui;

  const canSave = extractedData !== null && !cuiMismatch;

  const mappedResult: MappedRiskAnalysisResult | null = useMemo(() => {
    if (!currentSessionId || !extractedData) return null;
    return mapExtractedSectorsToBulkItems(
      currentSessionId,
      extractedData.sectorResults
    );
  }, [currentSessionId, extractedData]);

  const handleUpload = useCallback(async (files: File[]) => {
    setIsExtracting(true);
    setUploadError("");
    setExtractedData(null);

    try {
      const result = await extractDataFromFiles({
        files,
        documentType: "risk_analysis",
      });

      if (!result.isValid || result.errors.length > 0) {
        setUploadError(
          result.errors[0]?.message ?? "Eroare la procesarea fișierului."
        );
        return;
      }

      const riskData = result.mergedData.riskAnalysis;
      if (!riskData || riskData.sectorResults.length === 0) {
        setUploadError(
          "Nu s-au putut extrage date de analiză de risc din fișier."
        );
        return;
      }

      setExtractedData(riskData);
    } catch {
      setUploadError("Eroare la procesarea fișierului.");
    } finally {
      setIsExtracting(false);
    }
  }, []);

  const performInsert = useCallback(
    (redirectToWizard: boolean) => {
      if (!mappedResult) return;

      bulkInsertMutation.mutate(
        { records: mappedResult.records },
        {
          onSuccess: (response) => {
            if (!response.success) {
              toast({
                title: "Eroare la salvare",
                description: response.error ?? "Încercați din nou.",
                variant: "destructive",
              });
              return;
            }
            toast({ title: "Analiza de risc a fost salvată cu succes." });
            if (redirectToWizard) {
              navigate(`/analiza-risc/${currentSessionId}`);
            } else {
              navigate("/dashboard");
            }
          },
          onError: () => {
            toast({
              title: "Eroare la salvare",
              description: "Încercați din nou.",
              variant: "destructive",
            });
          },
        }
      );
    },
    [mappedResult, bulkInsertMutation, navigate, currentSessionId, toast]
  );

  const handleSave = useCallback(
    (redirectToWizard: boolean) => {
      if (!mappedResult) return;

      if (hasExistingRiskData) {
        setPendingAction(redirectToWizard ? "wizard" : "save");
        return;
      }

      performInsert(redirectToWizard);
    },
    [mappedResult, hasExistingRiskData, performInsert]
  );

  const handleOverwriteConfirm = useCallback(() => {
    if (!existingRiskData?.risk_analyses || pendingAction === null) return;

    const existingIds = existingRiskData.risk_analyses.map((ra) => ra.id);
    const redirectToWizard = pendingAction === "wizard";

    setPendingAction(null);

    bulkDeleteMutation.mutate(
      { ids: existingIds },
      {
        onSuccess: (response) => {
          if (!response.success) {
            toast({
              title: "Eroare la ștergere",
              description:
                response.error ?? "Nu s-au putut șterge datele existente.",
              variant: "destructive",
            });
            return;
          }
          performInsert(redirectToWizard);
        },
        onError: () => {
          toast({
            title: "Eroare la ștergere",
            description:
              "Nu s-au putut șterge datele existente. Încercați din nou.",
            variant: "destructive",
          });
        },
      }
    );
  }, [
    existingRiskData,
    pendingAction,
    bulkDeleteMutation,
    performInsert,
    toast,
  ]);

  if (isLoadingProgress) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!notificareCompleted) {
    return (
      <LockedSectionNotice
        title="Secțiune indisponibilă"
        message="Analiza de risc devine disponibilă după finalizarea Formularului de Notificare."
        ctaLabel="Completează Formularul de Notificare"
        ctaTo="/onboarding/formular-notificare"
      />
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-blue-600" />
        <div>
          <h1 className="text-xl font-heading font-bold text-slate-900">
            Încărcare Analiză de Risc
          </h1>
          <p className="text-sm text-slate-500">
            Încarcă fișierul XLSX cu analiza de risc completată.
          </p>
        </div>
      </div>

      <FileUploadSection
        onUpload={handleUpload}
        isExtracting={isExtracting}
        accept=".xlsx,.xls"
      />

      {uploadError && (
        <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-lg">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {uploadError}
        </div>
      )}

      {extractedData && (
        <>
          {cuiMismatch && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  CUI diferit detectat
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  CUI-ul companiei din fișier (
                  <span className="font-mono">
                    {extractedData.entityData.cui}
                  </span>
                  ) nu corespunde cu CUI-ul companiei curente (
                  <span className="font-mono">{entityData?.cui}</span>).
                  Încărcați un fișier pentru entitatea corectă.
                </p>
              </div>
            </div>
          )}

          <div className="border rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-slate-800">Date Entitate</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {extractedData.entityData.denumire && (
                <div>
                  <span className="text-slate-500">Denumire:</span>{" "}
                  <span className="font-medium">
                    {extractedData.entityData.denumire}
                  </span>
                </div>
              )}
              {extractedData.entityData.cui && (
                <div>
                  <span className="text-slate-500">CUI:</span>{" "}
                  <span className="font-medium font-mono">
                    {extractedData.entityData.cui}
                  </span>
                </div>
              )}
              {extractedData.entityData.judet && (
                <div>
                  <span className="text-slate-500">Județ:</span>{" "}
                  <span className="font-medium">
                    {extractedData.entityData.judet}
                  </span>
                </div>
              )}
              {extractedData.entityData.dimensiune && (
                <div>
                  <span className="text-slate-500">Dimensiune:</span>{" "}
                  <span className="font-medium">
                    {extractedData.entityData.dimensiune}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="border rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-slate-800">
              Sectoare ({extractedData.sectorResults.length})
            </h3>
            <div className="space-y-2">
              {extractedData.sectorResults.map((sector) => {
                const modifications =
                  mappedResult?.modificationsBySector[sector.sectorId] ?? [];
                const hasModifications = modifications.length > 0;

                if (!hasModifications) {
                  return (
                    <div
                      key={sector.sectorId}
                      className="p-3 bg-slate-50 rounded-lg space-y-1"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2 min-w-0">
                          <Target className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                          <span className="text-sm font-medium">
                            {sector.sectorName}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 w-[200px] justify-end">
                          <span className="text-sm text-slate-500 whitespace-nowrap">
                            Scor: {sector.totalScore}
                          </span>
                          <CyFunLevelBadge
                            level={sector.requirementCategory as CyFunLevel}
                          />
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground italic pl-6">
                        Valori implicite DNSC
                      </p>
                    </div>
                  );
                }

                return (
                  <Collapsible key={sector.sectorId}>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <CollapsibleTrigger className="w-full">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2 min-w-0">
                            <Target className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                            <span className="text-sm font-medium text-left">
                              {sector.sectorName}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                              {modifications.length}{" "}
                              {modifications.length === 1
                                ? "modificare"
                                : "modificări"}
                            </span>
                            <span className="text-sm text-slate-500 whitespace-nowrap">
                              Scor: {sector.totalScore}
                            </span>
                            <CyFunLevelBadge
                              level={sector.requirementCategory as CyFunLevel}
                            />
                            <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SectorModifications modifications={modifications} />
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          </div>

          <OnboardingFooter>
            <Button
              onClick={() => handleSave(false)}
              disabled={!canSave || isSaving}
              className="min-w-[160px]"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Se salvează...
                </>
              ) : (
                "Salvează"
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSave(true)}
              disabled={!canSave || isSaving}
              className="min-w-[160px]"
            >
              Editează în Wizard
            </Button>
          </OnboardingFooter>
        </>
      )}

      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suprascriere Analiză de Risc</AlertDialogTitle>
            <AlertDialogDescription>
              Aveți deja o analiză de risc cu{" "}
              {existingRiskData?.risk_analyses?.length ?? 0} sectoare (
              {existingRiskData?.completed_sectors ?? 0} finalizate). Salvarea
              va șterge datele existente și le va înlocui cu cele din fișierul
              încărcat. Această acțiune nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={handleOverwriteConfirm}>
              Suprascrie și Salvează
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
